import type { CutRange } from '@vidly/types';

export interface RenderResult {
  blob: Blob;
  mimeType: string;
  extension: string;
  durationSeconds: number;
}

/**
 * Client-Side In-Browser Video Cutter and Assembler
 * 
 * Accurately extracts and concatenates the selected cut intervals directly in the browser
 * using Canvas, Web Audio API, and MediaRecorder.
 * Completely serverless, works 100% on Cloudflare Pages and offline, and produces a real,
 * downloadable edited video file.
 */
export async function renderVideoCutsInBrowser(
  file: File | Blob,
  rawRanges: CutRange[],
  onProgress?: (percent: number) => void
): Promise<RenderResult> {
  if (!rawRanges || rawRanges.length === 0) {
    throw new Error('No se han seleccionado fragmentos para recortar.');
  }

  // 1. Sanitize, sort, and merge overlapping cut intervals
  const sorted = [...rawRanges].sort((a, b) => a.start - b.start);
  const ranges: { start: number; end: number }[] = [];

  for (const r of sorted) {
    const s = Math.max(0, r.start);
    const e = Math.max(s + 0.1, r.end);
    if (ranges.length === 0) {
      ranges.push({ start: s, end: e });
    } else {
      const prev = ranges[ranges.length - 1];
      if (s <= prev.end + 0.1) {
        prev.end = Math.max(prev.end, e);
      } else {
        ranges.push({ start: s, end: e });
      }
    }
  }

  const totalTargetDuration = ranges.reduce((acc, r) => acc + (r.end - r.start), 0);

  return new Promise<RenderResult>((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    video.playsInline = true;
    video.preload = 'auto';
    video.muted = false; // Muted false so AudioContext captures stream, but we don't connect to speakers
    video.volume = 1.0;

    let audioCtx: AudioContext | null = null;
    let audioSource: MediaElementAudioSourceNode | null = null;
    let audioDest: MediaStreamAudioDestinationNode | null = null;
    let recorder: MediaRecorder | null = null;
    let animationFrameId: number | null = null;
    let isTerminated = false;
    let safetyTimeout: any = null;

    const cleanup = () => {
      isTerminated = true;
      if (safetyTimeout) clearTimeout(safetyTimeout);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      video.pause();
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
      if (audioCtx && audioCtx.state !== 'closed') {
        audioCtx.close().catch(() => {});
      }
    };

    // Global fail-safe timeout (max 10 minutes for video processing)
    safetyTimeout = setTimeout(() => {
      cleanup();
      reject(new Error('El tiempo de renderizado excedió el límite permitido.'));
    }, 600000);

    video.onerror = () => {
      cleanup();
      reject(new Error('No se pudo decodificar el archivo de vídeo en el navegador.'));
    };

    video.onloadedmetadata = async () => {
      try {
        const vidDuration = video.duration || totalTargetDuration;

        // Clamp ranges within video duration
        const clampedRanges = ranges
          .map(r => ({
            start: Math.min(vidDuration, Math.max(0, r.start)),
            end: Math.min(vidDuration, Math.max(0, r.end))
          }))
          .filter(r => r.end > r.start + 0.1);

        if (clampedRanges.length === 0) {
          cleanup();
          reject(new Error('Los intervalos seleccionados quedan fuera de la duración del metraje.'));
          return;
        }

        // Set up Canvas to capture video frames
        const canvas = document.createElement('canvas');
        const maxDim = 1920;
        let width = video.videoWidth || 1280;
        let height = video.videoHeight || 720;
        if (width > maxDim || height > maxDim) {
          const ratio = maxDim / Math.max(width, height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { alpha: false });

        if (!ctx) {
          cleanup();
          reject(new Error('No se pudo inicializar el contexto gráfico 2D del navegador.'));
          return;
        }

        // Set up silent Web Audio processing pipeline
        let audioTrack: MediaStreamTrack | null = null;
        try {
          const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
          if (AudioContextClass) {
            audioCtx = new AudioContextClass();
            audioSource = audioCtx.createMediaElementSource(video);
            audioDest = audioCtx.createMediaStreamDestination();
            audioSource.connect(audioDest);
            // CRITICAL: audioSource is NOT connected to audioCtx.destination,
            // so processing is 100% silent and doesn't disturb the user!
            const tracks = audioDest.stream.getAudioTracks();
            if (tracks.length > 0) {
              audioTrack = tracks[0];
            }
          }
        } catch (audioErr) {
          console.warn('[VideoCutter] Web Audio no disponible o vídeo sin audio:', audioErr);
        }

        // Capture video track from Canvas
        const canvasStream = canvas.captureStream ? canvas.captureStream(30) : (canvas as any).mozCaptureStream(30);
        const videoTrack = canvasStream.getVideoTracks()[0];

        if (!videoTrack) {
          cleanup();
          reject(new Error('No se pudo capturar la pista de vídeo desde el lienzo.'));
          return;
        }

        // Combine into unified MediaStream
        const tracksToRecord: MediaStreamTrack[] = [videoTrack];
        if (audioTrack) {
          tracksToRecord.push(audioTrack);
        }
        const recordStream = new MediaStream(tracksToRecord);

        // Pick best supported container/codec
        let selectedMime = 'video/webm';
        let extension = 'webm';

        const preferredTypes = [
          { mime: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"', ext: 'mp4' },
          { mime: 'video/mp4', ext: 'mp4' },
          { mime: 'video/webm; codecs=vp9,opus', ext: 'webm' },
          { mime: 'video/webm; codecs=vp8,opus', ext: 'webm' },
          { mime: 'video/webm', ext: 'webm' }
        ];

        for (const t of preferredTypes) {
          if (MediaRecorder.isTypeSupported(t.mime)) {
            selectedMime = t.mime;
            extension = t.ext;
            break;
          }
        }

        recorder = new MediaRecorder(recordStream, {
          mimeType: selectedMime,
          videoBitsPerSecond: 4000000 // 4 Mbps high quality
        });

        const chunks: BlobPart[] = [];
        recorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) {
            chunks.push(e.data);
          }
        };

        recorder.onerror = (e: any) => {
          cleanup();
          reject(new Error('Error durante la grabación de medios: ' + (e?.error?.message || 'Error desconocido')));
        };

        recorder.onstop = () => {
          const finalBlob = new Blob(chunks, { type: selectedMime });
          const cutDuration = clampedRanges.reduce((acc, r) => acc + (r.end - r.start), 0);
          cleanup();
          resolve({
            blob: finalBlob,
            mimeType: selectedMime,
            extension,
            durationSeconds: Math.round(cutDuration * 10) / 10
          });
        };

        // Stepping & recording state
        let currentRangeIndex = 0;
        let cumulativeDuration = 0;

        // Helper to safely seek
        const seekTo = (targetSec: number): Promise<void> => {
          return new Promise<void>((seekResolve) => {
            const onSeeked = () => {
              video.removeEventListener('seeked', onSeeked);
              seekResolve();
            };
            video.addEventListener('seeked', onSeeked);
            video.currentTime = targetSec;
          });
        };

        // Prepare first range
        await seekTo(clampedRanges[0].start);
        if (isTerminated) return;

        // Start recorder
        recorder.start(100);
        if (audioCtx && audioCtx.state === 'suspended') {
          await audioCtx.resume();
        }

        // Play video
        await video.play();

        // Frame rendering loop
        const processFrame = () => {
          if (isTerminated) return;

          const currentRange = clampedRanges[currentRangeIndex];
          if (!currentRange) {
            if (recorder && recorder.state === 'recording') {
              recorder.stop();
            }
            return;
          }

          // Draw current frame to canvas
          ctx.drawImage(video, 0, 0, width, height);

          // Calculate overall progress percentage
          const elapsedInCurrent = Math.max(0, video.currentTime - currentRange.start);
          const totalElapsed = cumulativeDuration + elapsedInCurrent;
          const pct = Math.min(99, Math.round((totalElapsed / totalTargetDuration) * 100));
          if (onProgress) {
            onProgress(pct);
          }

          // Check if current range has finished
          if (video.currentTime >= currentRange.end - 0.05) {
            cumulativeDuration += (currentRange.end - currentRange.start);
            currentRangeIndex++;

            if (currentRangeIndex < clampedRanges.length) {
              const nextRange = clampedRanges[currentRangeIndex];
              video.pause();
              seekTo(nextRange.start).then(() => {
                if (!isTerminated) {
                  video.play().then(() => {
                    animationFrameId = requestAnimationFrame(processFrame);
                  }).catch(reject);
                }
              });
              return;
            } else {
              // Finished all ranges!
              video.pause();
              if (onProgress) onProgress(100);
              if (recorder && recorder.state === 'recording') {
                recorder.stop();
              }
              return;
            }
          }

          animationFrameId = requestAnimationFrame(processFrame);
        };

        // Fallback listener for background tab throttling
        video.ontimeupdate = () => {
          if (!isTerminated && animationFrameId === null) {
            processFrame();
          }
        };

        animationFrameId = requestAnimationFrame(processFrame);
      } catch (err: any) {
        cleanup();
        reject(err);
      }
    };
  });
}
